import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Popover,
  PopoverBody,
  PopoverHeader,
  Row,
  Spinner,
  TabContent,
  Table,
  TabPane,
} from "reactstrap";
import cogoToast from "cogo-toast";
import {
  FaChartPie,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaEye,
  FaInfoCircle,
  FaSearch,
  FaSync,
} from "react-icons/fa";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import {
  createWorkforcePlanningReviewApi,
  getWorkforcePlanningDetailsApi,
  updateWorkforcePlanningCdActionApi,
  updateWorkforcePlanningCommitteeDecisionApi,
  updateWorkforcePlanningReviewApi,
  workforcePlanningReportApi,
} from "../../../services/Api";
import { SUCCESS_RESULT } from "../../../Constants";
import ability from "../../config/defineAbility";
import { PERMS } from "../../config/perms";
import {
  CD_APPROVAL_OPTIONS,
  COMMITTEE_DECISION_OPTIONS,
  COMMITTEE_REVIEW_SCORE_CATEGORY_BANDS,
  COMMITTEE_REVIEW_SCORE_FORMULA,
  CRITICALITY_OPTIONS,
  applyCommitteeReviewDerivedFields,
  criticalityColor,
  decisionColor,
  EMPTY_WORKFORCE_REVIEW_FORM,
  formatCell,
  formatDate,
  formatMoney,
  buildWorkforceReviewPayload,
  getCommitteeReviewScoreBreakdown,
  hasCommitteeReview,
  isCommitteeReviewScoreInputField,
  reviewRecordToForm,
  getPrimaryCommitteeReview,
  WORKFORCE_REVIEW_DROPDOWN_OPTIONS,
} from "./workforcePlanningHelpers";
import WorkforcePlanningDetailsContent from "./WorkforcePlanningDetailsContent";
import "./WorkforcePlanningList.css";
import "./WorkforcePlanningAnalysis.css";

const ReviewSelect = ({
  label,
  name,
  value,
  onChange,
  options,
  disabled,
  md = 4,
}) => (
  <Col md={md}>
    <FormGroup>
      <Label>{label}</Label>
      <Input
        type="select"
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
      >
        <option value="">Select…</option>
        {(options || []).map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </Input>
    </FormGroup>
  </Col>
);

const CHART_PALETTE = [
  "#4e79a7",
  "#f28e2b",
  "#e15759",
  "#76b7b2",
  "#59a14f",
  "#edc948",
  "#b07aa1",
  "#ff9da7",
  "#9c755f",
  "#bab0ac",
];

const CRITICALITY_CHART_COLORS = {
  "Mission Critical": "#dc3545",
  Critical: "#dc3545",
  Essential: "#ffc107",
  High: "#ffc107",
  "Important / Flexible": "#0dcaf0",
  Medium: "#0dcaf0",
  "Non-Critical / Optional": "#6c757d",
  Low: "#6c757d",
};

const COMMITTEE_REVIEW_CHART_COLORS = {
  Reviewed: "#198754",
  "Not reviewed": "#adb5bd",
};

const STAFFING_GAP_COLORS = {
  "Staff gap": "#dc3545",
  Balanced: "#6c757d",
  "Staff surplus": "#198754",
  Unknown: "#adb5bd",
};

const countBy = (items, getKey, fallback = "Unknown") => {
  const counts = {};
  items.forEach((item) => {
    const raw = getKey(item);
    const key =
      raw == null || String(raw).trim() === "" ? fallback : String(raw).trim();
    counts[key] = (counts[key] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
};

const limitWithOther = (data, limit = 5) => {
  if (data.length <= limit) return data;
  const top = data.slice(0, limit);
  const otherCount = data.slice(limit).reduce((sum, item) => sum + item.value, 0);
  if (otherCount > 0) {
    top.push({ name: "Other", value: otherCount });
  }
  return top;
};

const colorForItem = (name, colorMap, index) =>
  colorMap[name] || CHART_PALETTE[index % CHART_PALETTE.length];

const getStableGapCategory = (row) => {
  const n = Number(row.stable_gap_surplus);
  if (Number.isNaN(n)) return "Unknown";
  if (n < 0) return "Staff gap";
  if (n > 0) return "Staff surplus";
  return "Balanced";
};

const AnalysisPieChart = ({ title, data, colorMap = {} }) => {
  const chartData = useMemo(
    () =>
      data.map((item, index) => ({
        ...item,
        color: colorForItem(item.name, colorMap, index),
      })),
    [data, colorMap]
  );

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="wfp-analysis-chart-item">
      <div className="wfp-analysis-chart-title">{title}</div>
      {total === 0 ? (
        <div className="wfp-analysis-chart-empty">No data</div>
      ) : (
        <>
          <div className="wfp-analysis-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={22}
                  outerRadius={48}
                  paddingAngle={1}
                  stroke="#fff"
                  strokeWidth={1}
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [`${value} (${Math.round((value / total) * 100)}%)`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="wfp-analysis-chart-legend">
            {chartData.map((entry) => (
              <div key={entry.name} className="wfp-analysis-chart-legend-item">
                <span
                  className="wfp-analysis-chart-legend-dot"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-truncate" title={entry.name}>
                  {entry.name} ({entry.value})
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const EMPTY_FILTERS = {
  office: "",
  area: "",
  criticality_category: "",
  committee_decision: "",
  role_id: "",
  project_name: "",
  created_by: "",
};

const EMPTY_DECISION_FORM = {
  committee_decision: "",
  comments: "",
  cd_approval: "",
  cd_comments: "",
};

const WorkforcePlanningAnalysis = () => {
  const canUpdateCd = ability.can(
    PERMS.UPDATE,
    PERMS.CAN_UPDATE_CD_APPROVAL_WORKFORCE_PLANNING
  );

  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });
  const [loading, setLoading] = useState(true);
  const [decisionModalOpen, setDecisionModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [decisionForm, setDecisionForm] = useState({ ...EMPTY_DECISION_FORM });
  const [savingDecision, setSavingDecision] = useState(false);
  const [chartsOpen, setChartsOpen] = useState(true);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsRecord, setDetailsRecord] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewModalTab, setReviewModalTab] = useState("review");
  const [reviewRow, setReviewRow] = useState(null);
  const [reviewForm, setReviewForm] = useState({ ...EMPTY_WORKFORCE_REVIEW_FORM });
  const [savingReview, setSavingReview] = useState(false);
  const [reviewDetailsRecord, setReviewDetailsRecord] = useState(null);
  const [reviewDetailsLoading, setReviewDetailsLoading] = useState(false);
  const [scoreInfoOpen, setScoreInfoOpen] = useState(false);
  const [categoryInfoOpen, setCategoryInfoOpen] = useState(false);

  useEffect(() => {
    fetchReport(appliedFilters);
    setSelectedDepartment("");
  }, [appliedFilters]);

  const getDepartmentName = (row) => {
    const name =
      row?.department_name ||
      row?.department?.department_name ||
      row?.role?.department_name ||
      row?.role?.department?.department_name ||
      "";
    const trimmed = String(name).trim();
    return trimmed || "Unassigned";
  };

  const buildFilterBody = (filterPayload = {}) => {
    const body = {};
    Object.entries(filterPayload).forEach(([key, value]) => {
      if (value === null || value === undefined || String(value).trim() === "") {
        return;
      }
      if (key === "created_by") {
        const n = parseInt(String(value).trim(), 10);
        if (!Number.isNaN(n)) body.created_by = n;
        return;
      }
      body[key] = String(value).trim();
    });
    return body;
  };

  const fetchReport = (filterPayload = {}) => {
    setLoading(true);
    workforcePlanningReportApi(buildFilterBody(filterPayload))
      .then((res) => {
        setLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          const data = res.data.data;
          setRows(Array.isArray(data) ? data : []);
        } else {
          setRows([]);
          cogoToast.error(res.data?.message || "Failed to load report", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong loading the report.",
          { hideAfter: 5 }
        );
      });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = (e) => {
    e?.preventDefault?.();
    setAppliedFilters({ ...filters });
  };

  const clearFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    setAppliedFilters({ ...EMPTY_FILTERS });
    setSelectedDepartment("");
  };

  const openDecisionModal = (row) => {
    setSelectedRow(row);
    setDecisionForm({
      committee_decision: row.committee_decision || "",
      comments: row.comments || "",
      cd_approval: row.cd_approval || "",
      cd_comments: row.cd_comments || "",
    });
    setDecisionModalOpen(true);
  };

  const closeDecisionModal = () => {
    setDecisionModalOpen(false);
    setSelectedRow(null);
    setDecisionForm({ ...EMPTY_DECISION_FORM });
  };

  const openDetailsModal = (row) => {
    if (!row?.id) return;
    setDetailsModalOpen(true);
    setDetailsRecord(null);
    setDetailsLoading(true);
    getWorkforcePlanningDetailsApi({ id: row.id })
      .then((res) => {
        setDetailsLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          setDetailsRecord(res.data.data || null);
        } else {
          cogoToast.error(res.data?.message || "Failed to load details", {
            hideAfter: 5,
          });
          setDetailsModalOpen(false);
        }
      })
      .catch((err) => {
        setDetailsLoading(false);
        setDetailsModalOpen(false);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong loading details.",
          { hideAfter: 5 }
        );
      });
  };

  const closeDetailsModal = () => {
    setDetailsModalOpen(false);
    setDetailsRecord(null);
    setDetailsLoading(false);
  };

  const loadReviewDetails = (id) => {
    if (!id) return;
    setReviewDetailsLoading(true);
    setReviewDetailsRecord(null);
    getWorkforcePlanningDetailsApi({ id })
      .then((res) => {
        setReviewDetailsLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          setReviewDetailsRecord(res.data.data || null);
        } else {
          cogoToast.error(res.data?.message || "Failed to load details", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setReviewDetailsLoading(false);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong loading details.",
          { hideAfter: 5 }
        );
      });
  };

  const openReviewModal = (row) => {
    if (!row?.id) return;
    setReviewRow(row);
    setReviewForm(reviewRecordToForm(getPrimaryCommitteeReview(row), row));
    setReviewModalTab("review");
    setScoreInfoOpen(false);
    setCategoryInfoOpen(false);
    setReviewModalOpen(true);
    loadReviewDetails(row.id);
  };

  const closeReviewModal = () => {
    if (savingReview) return;
    setReviewModalOpen(false);
    setReviewRow(null);
    setReviewForm({ ...EMPTY_WORKFORCE_REVIEW_FORM });
    setReviewModalTab("review");
    setReviewDetailsRecord(null);
    setReviewDetailsLoading(false);
    setScoreInfoOpen(false);
    setCategoryInfoOpen(false);
  };

  const handleReviewChange = (e) => {
    const { name, value } = e.target;
    setReviewForm((prev) => {
      const next = { ...prev, [name]: value };
      if (isCommitteeReviewScoreInputField(name)) {
        return applyCommitteeReviewDerivedFields(next);
      }
      return next;
    });
  };

  const saveReview = () => {
    if (!reviewRow?.id) return;
    setSavingReview(true);
    const payload = buildWorkforceReviewPayload(reviewRow.id, reviewForm);
    const isEdit = Boolean(reviewForm.review_id);
    const request = isEdit
      ? updateWorkforcePlanningReviewApi(payload)
      : createWorkforcePlanningReviewApi(payload);
    request
      .then((res) => {
        setSavingReview(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          cogoToast.success(
            res.data.message || (isEdit ? "Review updated" : "Review submitted"),
            { hideAfter: 3 }
          );
          setReviewModalOpen(false);
          setReviewRow(null);
          setReviewForm({ ...EMPTY_WORKFORCE_REVIEW_FORM });
          fetchReport(appliedFilters);
        } else {
          cogoToast.error(res.data?.message || "Failed to submit review", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setSavingReview(false);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong submitting the review.",
          { hideAfter: 5 }
        );
      });
  };

  const handleDecisionChange = (e) => {
    const { name, value } = e.target;
    setDecisionForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveDecision = () => {
    if (!selectedRow?.id) return;

    const payload = { id: selectedRow.id };
    const fields = canUpdateCd
      ? ["cd_approval", "cd_comments"]
      : ["committee_decision", "comments"];

    fields.forEach((key) => {
      const value =
        typeof decisionForm[key] === "string"
          ? decisionForm[key].trim()
          : decisionForm[key];
      if (value !== "" && value != null) {
        payload[key] = value;
      }
    });

    if (Object.keys(payload).length < 2) {
      cogoToast.error(
        canUpdateCd
          ? "Provide at least one of: CD decision or CD comments."
          : "Provide at least one of: committee decision or comments.",
        { hideAfter: 4 }
      );
      return;
    }

    setSavingDecision(true);
    const request = canUpdateCd
      ? updateWorkforcePlanningCdActionApi(payload)
      : updateWorkforcePlanningCommitteeDecisionApi(payload);

    request
      .then((res) => {
        setSavingDecision(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          cogoToast.success(res.data.message || "Decision updated", { hideAfter: 3 });
          const updated = res.data.data;
          if (updated?.id) {
            setRows((prev) =>
              prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row))
            );
          } else {
            fetchReport(appliedFilters);
          }
          closeDecisionModal();
        } else {
          cogoToast.error(res.data?.message || "Failed to update decision", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setSavingDecision(false);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong updating the decision.",
          { hideAfter: 5 }
        );
      });
  };

  const chartData = useMemo(() => {
    return {
      criticality: countBy(rows, (row) => row.criticality_category),
      committeeReview: countBy(rows, (row) =>
        hasCommitteeReview(row) ? "Reviewed" : "Not reviewed"
      ),
      office: limitWithOther(countBy(rows, (row) => row.office)),
      area: limitWithOther(countBy(rows, (row) => row.area)),
      staffingGap: countBy(rows, getStableGapCategory),
      role: limitWithOther(
        countBy(rows, (row) => row.role?.role_name?.trim() || "Unknown role")
      ),
    };
  }, [rows]);

  const departmentOptions = useMemo(() => {
    const names = new Set();
    rows.forEach((row) => names.add(getDepartmentName(row)));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!selectedDepartment) return rows;
    return rows.filter(
      (row) => getDepartmentName(row) === selectedDepartment
    );
  }, [rows, selectedDepartment]);

  return (
    <div className="container-fluid px-3 px-lg-4 mt-4 mb-5 wfp-list">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h2 className="page-title mb-1">Workforce Planning Analysis</h2>
          <div className="text-muted">
            HRM · Organisation-wide workforce planning report
          </div>
        </div>
        <Button color="secondary" outline onClick={() => fetchReport(appliedFilters)}>
          <FaSync className="me-1" /> Refresh
        </Button>
      </div>

      <Card className="shadow-sm mb-3">
        <CardBody>
          <form onSubmit={applyFilters}>
            <Row className="g-2 align-items-end">
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Label>Office</Label>
                  <Input
                    name="office"
                    value={filters.office}
                    onChange={handleFilterChange}
                    placeholder="Office"
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Label>Area</Label>
                  <Input
                    name="area"
                    value={filters.area}
                    onChange={handleFilterChange}
                    placeholder="Area"
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Label>Criticality</Label>
                  <Input
                    type="select"
                    name="criticality_category"
                    value={filters.criticality_category}
                    onChange={handleFilterChange}
                  >
                    <option value="">All</option>
                    {CRITICALITY_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Label>Committee decision</Label>
                  <Input
                    type="select"
                    name="committee_decision"
                    value={filters.committee_decision}
                    onChange={handleFilterChange}
                  >
                    <option value="">All</option>
                    {COMMITTEE_DECISION_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup className="mb-2">
                  <Label>Project</Label>
                  <Input
                    name="project_name"
                    value={filters.project_name}
                    onChange={handleFilterChange}
                    placeholder="Project name"
                  />
                </FormGroup>
              </Col>
              <Col md={1}>
                <FormGroup className="mb-2">
                  <Label>Role ID</Label>
                  <Input
                    name="role_id"
                    value={filters.role_id}
                    onChange={handleFilterChange}
                    placeholder="UUID"
                  />
                </FormGroup>
              </Col>
              <Col md={1}>
                <FormGroup className="mb-2">
                  <Label>Created by</Label>
                  <Input
                    name="created_by"
                    type="number"
                    value={filters.created_by}
                    onChange={handleFilterChange}
                    placeholder="User ID"
                  />
                </FormGroup>
              </Col>
              <Col md={12} className="d-flex gap-2">
                <Button color="primary" type="submit">
                  <FaSearch className="me-1" /> Apply filters
                </Button>
                <Button color="secondary" outline type="button" onClick={clearFilters}>
                  Clear
                </Button>
              </Col>
            </Row>
          </form>
        </CardBody>
      </Card>

      <Card className="shadow-sm mb-3 wfp-analysis-charts-card">
        <CardBody className="pb-2">
          <button
            type="button"
            className="wfp-charts-toggle w-100 d-flex justify-content-between align-items-center"
            onClick={() => setChartsOpen((open) => !open)}
            aria-expanded={chartsOpen}
          >
            <span className="fw-semibold">
              <FaChartPie className="me-2 text-primary" />
              Report summary charts
              {!loading && (
                <small className="text-muted fw-normal ms-2">
                  {rows.length} record(s)
                </small>
              )}
            </span>
            {chartsOpen ? <FaChevronUp /> : <FaChevronDown />}
          </button>

          <Collapse isOpen={chartsOpen}>
            {loading ? (
              <div className="text-center py-4">
                <Spinner size="sm" color="primary" />
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center text-muted py-4 small">
                Apply filters or refresh to load chart data.
              </div>
            ) : (
              <Row className="g-2 mt-2">
                <Col xs={6} md={4} lg={2}>
                  <AnalysisPieChart
                    title="Criticality"
                    data={chartData.criticality}
                    colorMap={CRITICALITY_CHART_COLORS}
                  />
                </Col>
                <Col xs={6} md={4} lg={2}>
                  <AnalysisPieChart
                    title="Committee Review"
                    data={chartData.committeeReview}
                    colorMap={COMMITTEE_REVIEW_CHART_COLORS}
                  />
                </Col>
                <Col xs={6} md={4} lg={2}>
                  <AnalysisPieChart
                    title="Office"
                    data={chartData.office}
                  />
                </Col>
                <Col xs={6} md={4} lg={2}>
                  <AnalysisPieChart
                    title="Area"
                    data={chartData.area}
                  />
                </Col>
                <Col xs={6} md={4} lg={2}>
                  <AnalysisPieChart
                    title="Stable staffing"
                    data={chartData.staffingGap}
                    colorMap={STAFFING_GAP_COLORS}
                  />
                </Col>
                <Col xs={6} md={4} lg={2}>
                  <AnalysisPieChart
                    title="Role"
                    data={chartData.role}
                  />
                </Col>
              </Row>
            )}
          </Collapse>
        </CardBody>
      </Card>

      <Card className="shadow-sm">
        <CardBody>
          <div className="d-flex justify-content-between align-items-end mb-3 flex-wrap gap-3">
            <div>
              <div className="fw-semibold">Workforce planning records</div>
              <div className="text-muted small">
                {loading ? "Loading…" : `${visibleRows.length} record(s)`}
              </div>
            </div>
            <div className="wfp-dept-filter">
              <label className="wfp-dept-filter__label" htmlFor="wfp-dept-filter-select">
                Department
              </label>
              <div className="wfp-dept-filter__control">
                <Input
                  id="wfp-dept-filter-select"
                  type="select"
                  className="wfp-dept-filter__select"
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                >
                  <option value="">All departments</option>
                  {departmentOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Input>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="text-center text-muted py-5">
              No workforce planning records found.
            </div>
          ) : (
            <div className="table-responsive wfp-list-table-wrap">
              <Table hover className="align-middle mb-0 wfp-list-table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Office</th>
                    <th>Area</th>
                    <th>Created by</th>
                    <th>Monthly salary</th>
                    <th>Months</th>
                    <th>Total salary</th>
                    <th>Score</th>
                    <th>Criticality</th>
                    <th>HoD recommendation</th>
                    <th>Committee decision</th>
                    <th>CD decision</th>
                    <th>Updated</th>
                    <th style={{ width: 180 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => {
                    const reviewed = hasCommitteeReview(row);
                    return (
                      <tr
                        key={row.id}
                        style={{ cursor: "pointer" }}
                        onClick={() => openDetailsModal(row)}
                      >
                        <td>
                          <div className="fw-semibold">
                            {formatCell(row.role?.role_name)}
                          </div>
                          <small className="text-muted">
                            {formatCell(row.project_name)}
                          </small>
                        </td>
                        <td>{formatCell(row.office)}</td>
                        <td>{formatCell(row.area)}</td>
                        <td>
                          <div>{formatCell(row.creator?.name)}</div>
                          <small className="text-muted">
                            {formatCell(row.creator?.email)}
                          </small>
                        </td>
                        <td>{formatMoney(row.monthly_gross_salary)}</td>
                        <td>{formatCell(row.nbr_of_months)}</td>
                        <td>{formatMoney(row.total_salary)}</td>
                        <td>{formatCell(row.total_score)}</td>
                        <td>
                          <Badge color={criticalityColor(row.criticality_category)}>
                            {formatCell(row.criticality_category)}
                          </Badge>
                        </td>
                        <td style={{ whiteSpace: "normal", minWidth: 160 }}>
                          {formatCell(row.hod_requested_action_recommendation)}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex flex-column align-items-start gap-1">
                            {reviewed ? (
                              <span
                                className="wfp-review-check text-success"
                                title="Committee review submitted"
                              >
                                <FaCheckCircle />
                                <span>Reviewed</span>
                              </span>
                            ) : null}
                            {row.committee_decision ? (
                              <Badge color={decisionColor(row.committee_decision)}>
                                {row.committee_decision}
                              </Badge>
                            ) : null}
                            <Button
                              size="sm"
                              color="success"
                              outline={!reviewed}
                              onClick={() => openReviewModal(row)}
                            >
                              {reviewed ? "Edit Review" : "Review"}
                            </Button>
                          </div>
                        </td>
                        <td>
                          <Badge color={decisionColor(row.cd_approval)}>
                            {formatCell(row.cd_approval)}
                          </Badge>
                        </td>
                        <td>
                          <small>{formatDate(row.updated_at || row.created_at)}</small>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex gap-1 flex-wrap">
                            <Button
                              size="sm"
                              color="info"
                              outline
                              title="View details"
                              onClick={() => openDetailsModal(row)}
                            >
                              <FaEye />
                            </Button>
                            <Button
                              size="sm"
                              color="primary"
                              onClick={() => openDecisionModal(row)}
                            >
                              <FaEdit className="me-1" />{" "}
                              {canUpdateCd ? "Update CD action" : "Update decision"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={decisionModalOpen} toggle={closeDecisionModal} size="lg">
        <ModalHeader toggle={closeDecisionModal}>
          {canUpdateCd ? "Update CD action" : "Update committee decision"}
        </ModalHeader>
        <ModalBody>
          {selectedRow && (
            <div className="mb-3 p-3 rounded" style={{ background: "#f4f8fb" }}>
              <div className="fw-semibold">
                {formatCell(selectedRow.role?.role_name)}
              </div>
              <small className="text-muted">
                {formatCell(selectedRow.office)}
                {selectedRow.area ? ` · ${selectedRow.area}` : ""}
                {selectedRow.project_name ? ` · ${selectedRow.project_name}` : ""}
              </small>
              <div className="mt-2 d-flex gap-2 flex-wrap">
                <Badge color={criticalityColor(selectedRow.criticality_category)}>
                  {formatCell(selectedRow.criticality_category)}
                </Badge>
                <Badge color="light" className="text-dark">
                  Score {formatCell(selectedRow.total_score)}
                </Badge>
              </div>
              {selectedRow.hod_requested_action_recommendation ? (
                <div className="mt-2 small">
                  <span className="text-muted">HoD recommendation: </span>
                  {formatCell(selectedRow.hod_requested_action_recommendation)}
                </div>
              ) : null}
              {canUpdateCd && selectedRow.committee_decision ? (
                <div className="mt-1 small">
                  <span className="text-muted">Committee decision: </span>
                  {formatCell(selectedRow.committee_decision)}
                </div>
              ) : null}
            </div>
          )}

          {canUpdateCd ? (
            <>
              <FormGroup>
                <Label>CD decision</Label>
                <Input
                  type="select"
                  name="cd_approval"
                  value={decisionForm.cd_approval}
                  onChange={handleDecisionChange}
                >
                  <option value="">Select…</option>
                  {CD_APPROVAL_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>CD comments</Label>
                <Input
                  type="textarea"
                  rows={3}
                  name="cd_comments"
                  value={decisionForm.cd_comments}
                  onChange={handleDecisionChange}
                  placeholder="e.g. Budget confirmed; proceed with recruitment."
                />
              </FormGroup>
            </>
          ) : (
            <>
              <FormGroup>
                <Label>Committee decision</Label>
                <Input
                  type="select"
                  name="committee_decision"
                  value={decisionForm.committee_decision}
                  onChange={handleDecisionChange}
                >
                  <option value="">Select…</option>
                  {COMMITTEE_DECISION_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>Comments</Label>
                <Input
                  type="textarea"
                  rows={3}
                  name="comments"
                  value={decisionForm.comments}
                  onChange={handleDecisionChange}
                  placeholder="Committee notes"
                />
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeDecisionModal} disabled={savingDecision}>
            Cancel
          </Button>
          <Button color="primary" onClick={saveDecision} disabled={savingDecision}>
            {savingDecision ? (
              <>
                <Spinner size="sm" className="me-2" /> Saving…
              </>
            ) : canUpdateCd ? (
              "Save CD action"
            ) : (
              "Save decision"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={detailsModalOpen}
        toggle={closeDetailsModal}
        size="xl"
        className="wfp-analysis-details-modal"
        scrollable
      >
        <ModalHeader toggle={closeDetailsModal}>Workforce planning details</ModalHeader>
        <ModalBody className="wfp-analysis-details-modal__body">
          {detailsLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <WorkforcePlanningDetailsContent
              record={detailsRecord}
              showBackLink={false}
              showActions={false}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeDetailsModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={reviewModalOpen}
        toggle={closeReviewModal}
        size="xl"
        scrollable
        className="wfp-analysis-review-modal"
      >
        <ModalHeader toggle={closeReviewModal}>
          {reviewForm.review_id ? "Edit HR Sub-Committee Review" : "HR Sub-Committee Review"}
        </ModalHeader>
        <ModalBody>
          <Nav tabs className="mb-3 wfp-review-modal-tabs">
            <NavItem>
              <NavLink
                href="#"
                className={reviewModalTab === "review" ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  setReviewModalTab("review");
                }}
              >
                {reviewForm.review_id
                  ? "Edit HR Sub-Committee Review"
                  : "HR Sub-Committee Review"}
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                href="#"
                className={reviewModalTab === "details" ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  setReviewModalTab("details");
                }}
              >
                Workforce planning details
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={reviewModalTab}>
            <TabPane tabId="review">
              {reviewRow && (
                <div className="mb-3 p-3 rounded" style={{ background: "#f4f8fb" }}>
                  <div className="fw-semibold">
                    {formatCell(reviewRow.role?.role_name)}
                  </div>
                  <small className="text-muted">
                    {formatCell(reviewRow.office)}
                    {reviewRow.area ? ` · ${reviewRow.area}` : ""}
                    {reviewRow.project_name ? ` · ${reviewRow.project_name}` : ""}
                  </small>
                  <div className="mt-2 d-flex gap-2 flex-wrap">
                    <Badge color={criticalityColor(reviewRow.criticality_category)}>
                      {formatCell(reviewRow.criticality_category)}
                    </Badge>
                    <Badge color="light" className="text-dark">
                      Score {formatCell(reviewRow.total_score)}
                    </Badge>
                    <Badge color={decisionColor(reviewRow.committee_decision)}>
                      Committee: {formatCell(reviewRow.committee_decision)}
                    </Badge>
                  </div>
                </div>
              )}

              <Row className="g-2">
            <ReviewSelect
              label="Evidence quality"
              name="hr_sub_committee_evidence_quality"
              value={reviewForm.hr_sub_committee_evidence_quality}
              onChange={handleReviewChange}
              options={WORKFORCE_REVIEW_DROPDOWN_OPTIONS.hr_sub_committee_evidence_quality}
              disabled={savingReview}
            />
            <ReviewSelect
              label="Current staff verified"
              name="current_staff_verified"
              value={reviewForm.current_staff_verified}
              onChange={handleReviewChange}
              options={WORKFORCE_REVIEW_DROPDOWN_OPTIONS.current_staff_verified}
              disabled={savingReview}
            />
            <ReviewSelect
              label="Scenario validation"
              name="hr_sub_committee_scenario_validation"
              value={reviewForm.hr_sub_committee_scenario_validation}
              onChange={handleReviewChange}
              options={WORKFORCE_REVIEW_DROPDOWN_OPTIONS.hr_sub_committee_scenario_validation}
              disabled={savingReview}
            />

            <Col md={4}>
              <FormGroup>
                <Label>
                  Minimum staff needed{" "}
                  <span className="text-muted fw-normal">
                    (submitted: {formatCell(reviewRow?.minimum_staff_needed)})
                  </span>
                </Label>
                <Input
                  type="number"
                  name="minimum_staff_needed"
                  value={reviewForm.minimum_staff_needed}
                  onChange={handleReviewChange}
                  disabled={savingReview}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>
                  Stable staff needed{" "}
                  <span className="text-muted fw-normal">
                    (submitted: {formatCell(reviewRow?.stable_staff_needed)})
                  </span>
                </Label>
                <Input
                  type="number"
                  name="stable_staff_needed"
                  value={reviewForm.stable_staff_needed}
                  onChange={handleReviewChange}
                  disabled={savingReview}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>
                  Growth staff needed{" "}
                  <span className="text-muted fw-normal">
                    (submitted: {formatCell(reviewRow?.growth_staff_needed)})
                  </span>
                </Label>
                <Input
                  type="number"
                  name="growth_staff_needed"
                  value={reviewForm.growth_staff_needed}
                  onChange={handleReviewChange}
                  disabled={savingReview}
                />
              </FormGroup>
            </Col>

            <ReviewSelect
              label="Funding status"
              name="funding_status"
              value={reviewForm.funding_status}
              onChange={handleReviewChange}
              options={WORKFORCE_REVIEW_DROPDOWN_OPTIONS.funding_status}
              disabled={savingReview}
            />
            <Col md={4}>
              <FormGroup>
                <Label>Budget confirmed until</Label>
                <Input
                  type="date"
                  name="budget_confirmed_until"
                  value={reviewForm.budget_confirmed_until}
                  onChange={handleReviewChange}
                  disabled={savingReview}
                />
              </FormGroup>
            </Col>
            <ReviewSelect
              label="Can merge / share / defer"
              name="can_merge_share_defer"
              value={reviewForm.can_merge_share_defer}
              onChange={handleReviewChange}
              options={WORKFORCE_REVIEW_DROPDOWN_OPTIONS.can_merge_share_defer}
              disabled={savingReview}
            />

            <ReviewSelect
              md={3}
              label="Stress needed"
              name="stress_needed"
              value={reviewForm.stress_needed}
              onChange={handleReviewChange}
              options={WORKFORCE_REVIEW_DROPDOWN_OPTIONS.stress_needed}
              disabled={savingReview}
            />
            <ReviewSelect
              md={3}
              label="Risk level confirmed high"
              name="risk_level_confirmed_high"
              value={reviewForm.risk_level_confirmed_high}
              onChange={handleReviewChange}
              options={WORKFORCE_REVIEW_DROPDOWN_OPTIONS.risk_level_confirmed_high}
              disabled={savingReview}
            />
            <ReviewSelect
              md={3}
              label="Evidence clear"
              name="evidence_clear"
              value={reviewForm.evidence_clear}
              onChange={handleReviewChange}
              options={WORKFORCE_REVIEW_DROPDOWN_OPTIONS.evidence_clear}
              disabled={savingReview}
            />
            <ReviewSelect
              md={3}
              label="Cannot cover / merge"
              name="cannot_cover_merge"
              value={reviewForm.cannot_cover_merge}
              onChange={handleReviewChange}
              options={WORKFORCE_REVIEW_DROPDOWN_OPTIONS.cannot_cover_merge}
              disabled={savingReview}
            />

            <Col md={6}>
              <FormGroup>
                <Label>Core risk linked to</Label>
                <Input
                  type="text"
                  name="core_risk_linked_to"
                  value={reviewForm.core_risk_linked_to}
                  onChange={handleReviewChange}
                  disabled={savingReview}
                  placeholder="Describe core risk linkage"
                />
              </FormGroup>
            </Col>
            <ReviewSelect
              md={3}
              label="Funded or escalate or deferred"
              name="funded_or_escalate"
              value={reviewForm.funded_or_escalate}
              onChange={handleReviewChange}
              options={WORKFORCE_REVIEW_DROPDOWN_OPTIONS.funded_or_escalate}
              disabled={savingReview}
            />
            <Col md={3}>
              <FormGroup>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <Label className="mb-0">Committee review score</Label>
                  <button
                    type="button"
                    id="committee-review-score-info"
                    className="btn btn-link p-0 border-0"
                    style={{ color: "#0d6efd", lineHeight: 1 }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setScoreInfoOpen((open) => !open);
                    }}
                    aria-label="How committee review score is calculated"
                  >
                    <FaInfoCircle />
                  </button>
                </div>
                <Input
                  type="number"
                  name="committe_review_score"
                  value={reviewForm.committe_review_score}
                  readOnly
                  disabled={savingReview}
                />
                <Popover
                  placement="left"
                  isOpen={scoreInfoOpen}
                  target="committee-review-score-info"
                  toggle={() => {}}
                  trigger="legacy"
                >
                  <PopoverHeader>Committee review score formula</PopoverHeader>
                  <PopoverBody>
                    <div className="small mb-2">
                      Total is recalculated when scored fields change (max 100).
                    </div>
                    <ul className="small mb-2 ps-3">
                      {COMMITTEE_REVIEW_SCORE_FORMULA.map((item) => (
                        <li key={item.field} className="mb-1">
                          <strong>{item.label}</strong>: {item.rules}
                        </li>
                      ))}
                    </ul>
                    <div className="small border-top pt-2">
                      <div className="fw-semibold mb-1">Current breakdown</div>
                      {getCommitteeReviewScoreBreakdown(reviewForm).map((item) => (
                        <div
                          key={item.field}
                          className="d-flex justify-content-between gap-2"
                        >
                          <span>
                            {item.label}
                            {item.value !== "—" ? ` (${item.value})` : ""}
                          </span>
                          <span>{item.marks}</span>
                        </div>
                      ))}
                      <div className="d-flex justify-content-between fw-semibold mt-1">
                        <span>Total</span>
                        <span>{reviewForm.committe_review_score || 0}</span>
                      </div>
                    </div>
                  </PopoverBody>
                </Popover>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>Technical observation</Label>
                <Input
                  type="textarea"
                  rows={2}
                  name="hr_sub_committee_technical_observation"
                  value={reviewForm.hr_sub_committee_technical_observation}
                  onChange={handleReviewChange}
                  disabled={savingReview}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <Label className="mb-0">Proposed category</Label>
                  <button
                    type="button"
                    id="proposed-category-info"
                    className="btn btn-link p-0 border-0"
                    style={{ color: "#0d6efd", lineHeight: 1 }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCategoryInfoOpen((open) => !open);
                    }}
                    aria-label="How proposed category is calculated"
                  >
                    <FaInfoCircle />
                  </button>
                </div>
                <Input
                  type="text"
                  name="hr_sub_committee_proposed_category"
                  value={reviewForm.hr_sub_committee_proposed_category}
                  readOnly
                  disabled={savingReview}
                />
                <small className="text-muted">
                  {COMMITTEE_REVIEW_SCORE_CATEGORY_BANDS.find(
                    (band) =>
                      band.category === reviewForm.hr_sub_committee_proposed_category
                  )?.meaning || ""}
                </small>
                <Popover
                  placement="left"
                  isOpen={categoryInfoOpen}
                  target="proposed-category-info"
                  toggle={() => {}}
                  trigger="legacy"
                >
                  <PopoverHeader>Proposed category formula</PopoverHeader>
                  <PopoverBody>
                    <div className="small mb-2">
                      Derived from the committee review score:
                    </div>
                    <ul className="small mb-0 ps-3">
                      {COMMITTEE_REVIEW_SCORE_CATEGORY_BANDS.map((band) => (
                        <li key={band.category} className="mb-2">
                          <strong>
                            {band.min}–{band.max} → {band.category}
                          </strong>
                          <div className="text-muted">{band.meaning}</div>
                        </li>
                      ))}
                    </ul>
                  </PopoverBody>
                </Popover>
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label>Recommendation</Label>
                <Input
                  type="textarea"
                  rows={2}
                  name="hr_sub_committee_recommendation"
                  value={reviewForm.hr_sub_committee_recommendation}
                  onChange={handleReviewChange}
                  disabled={savingReview}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Committee justification</Label>
                <Input
                  type="textarea"
                  rows={2}
                  name="committee_justification"
                  value={reviewForm.committee_justification}
                  onChange={handleReviewChange}
                  disabled={savingReview}
                />
              </FormGroup>
            </Col>

            <Col md={4}>
              <FormGroup>
                <Label>Implementation action</Label>
                <Input
                  type="text"
                  name="implementation_action"
                  value={reviewForm.implementation_action}
                  onChange={handleReviewChange}
                  disabled={savingReview}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Responsible</Label>
                <Input
                  type="text"
                  name="responsible"
                  value={reviewForm.responsible}
                  onChange={handleReviewChange}
                  disabled={savingReview}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label>Due date</Label>
                <Input
                  type="date"
                  name="due_date"
                  value={reviewForm.due_date}
                  onChange={handleReviewChange}
                  disabled={savingReview}
                />
              </FormGroup>
            </Col>
            <ReviewSelect
              md={2}
              label="Implementation status"
              name="implementation_status"
              value={reviewForm.implementation_status}
              onChange={handleReviewChange}
              options={WORKFORCE_REVIEW_DROPDOWN_OPTIONS.implementation_status}
              disabled={savingReview}
            />
              </Row>
            </TabPane>

            <TabPane tabId="details">
              {reviewDetailsLoading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : (
                <div className="wfp-analysis-details-modal__body">
                  <WorkforcePlanningDetailsContent
                    record={reviewDetailsRecord}
                    showBackLink={false}
                    showActions={false}
                  />
                </div>
              )}
            </TabPane>
          </TabContent>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeReviewModal} disabled={savingReview}>
            Cancel
          </Button>
          {reviewModalTab === "review" ? (
            <Button color="primary" onClick={saveReview} disabled={savingReview}>
              {savingReview ? (
                <>
                  <Spinner size="sm" className="me-2" />{" "}
                  {reviewForm.review_id ? "Updating…" : "Submitting…"}
                </>
              ) : reviewForm.review_id ? (
                "Update review"
              ) : (
                "Submit review"
              )}
            </Button>
          ) : null}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default WorkforcePlanningAnalysis;
